/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file nominated apps store updater methods
 * @module Epic.AppOrchard.StoreMethods.NominatedApps
 */

import { IDispatch } from "@epic/react-redux-booster";
import { nominatedAppsActions } from "../state/widgets/nominatedApps";
import { INominatedApp, INotImplementingNowDecision } from "../types";

/**
 * Update store with nominated apps after loading from server
 * @param dispatch for useDispatch
 * @param orgId customer org id
 * @param nominatedApps nominated apps for customer org
 */
export function updateNominatedAppsAfterLoad(
	dispatch: IDispatch,
	orgId: number,
	nominatedApps: INominatedApp[],
	participantTermsId: number,
	apiLicenseAgreementId: number | null,
	hasSignedAnyApiLicenseAgreement: boolean,
	hasSignedCurrentApiLicenseAgreement: boolean,
) {
	dispatch(
		nominatedAppsActions.updateNominatedAppsAfterLoad({
			orgId,
			nominatedApps,
			participantTermsId,
			apiLicenseAgreementId,
			hasSignedAnyApiLicenseAgreement,
			hasSignedCurrentApiLicenseAgreement,
		}),
	);
}

/**
 * Update not implementing now information
 * @param dispatch for useDispatch
 * @param appId client app id
 * @param orgId customer org id
 * @param notNowDecision not implementing now details
 */
export function updateNotImplementingNowDetails(
	dispatch: IDispatch,
	orgId: number,
	appId: number,
	notNowDecision: INotImplementingNowDecision | null,
) {
	dispatch(nominatedAppsActions.updateNotImplementingNowDetails({ appId, orgId, notNowDecision }));
}

/**
 * Remove app from the set of nominated apps after the customer has requested a download from the vendor
 * @param dispatch for useDispatch
 * @param appId client app id
 * @param orgId customer org id
 */
export function removeAppAfterDownloadRequest(dispatch: IDispatch, orgId: number, appId: number) {
	dispatch(nominatedAppsActions.removeAppAfterDownloadRequest({ appId, orgId }));
}

/** Update state when org signed current API license terms
 * @param state current state
 * @param orgId customer org ID
 */
export function updateOrgSignedTerms(dispatch: IDispatch, orgId: number) {
	dispatch(nominatedAppsActions.updateOrgSignedTerms(orgId));
}
